import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router-dom";

import { ConfigService } from "@/shared/domain/config/ConfigService";
import {
  EMPTY_TABLE_SEARCH_PREFERENCES,
  type SearchTablePreferenceKey,
  getQueryKey,
  searchTablePreferencesQuery,
} from "@/shared/domain/config/hooks/useSearchTablePreferences";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { ErrorMonitoringService } from "@/shared/services";

/**
 * Loader function for populating table preferences. Table preferences
 * are treated as critical path data so we block on this data to avoid
 * layout shifts or additional loading mechanisms.
 *
 * This function treats all fetch failures as "no preferences set" so the
 * user still sees the default table.
 */
export function loadSearchTablePreferences(
  queryClient: QueryClient,
  configKey: SearchTablePreferenceKey,
): LoaderFunction {
  return async function loadTablePreferences() {
    const httpClient = await makeAuthenticatedHttpClient();
    const configService = new ConfigService(httpClient);

    try {
      // Populates the Tanstack Query cache with table preferences
      await queryClient.ensureQueryData(
        searchTablePreferencesQuery(configService, configKey),
      );
    } catch (error) {
      const isClientError =
        error instanceof RobotoDomainException && error.isClientError();

      if (!isClientError) {
        ErrorMonitoringService.captureError(error, {
          context: "searchTablePreferencesLoader",
          configKey,
        });
      }

      // Fetch failures still warm the cache with an empty sentinel
      queryClient.setQueryData(
        getQueryKey(configKey),
        EMPTY_TABLE_SEARCH_PREFERENCES,
      );
    }

    return null;
  };
}
